import { AxiosError } from 'axios';
import { WeTreeApiError, ApiResult, ConfigurationError } from './errors';

// Re-export ApiResult for convenience
export type { ApiResult } from './errors';

/**
 * Configuration for API wrapper behavior
 */
export interface ApiWrapperConfig {
  /** Whether to log errors to console in development */
  logErrors?: boolean;
  /** Custom error logger function */
  errorLogger?: (error: WeTreeApiError, context?: string) => void;
}

/**
 * Default configuration for the API wrapper
 */
const defaultConfig: ApiWrapperConfig = {
  logErrors: import.meta.env.DEV,
  errorLogger: (error: WeTreeApiError, context?: string) => {
    // eslint-disable-next-line no-console
    console.error(`API Error${context ? ` in ${context}` : ''}:`, {
      code: error.code,
      message: error.message,
      status: error.status,
      details: error.details,
      originalError: error.originalError
    });
  }
};

/**
 * Global configuration for the API wrapper
 */
let globalConfig: ApiWrapperConfig = { ...defaultConfig };

/**
 * Configure the API wrapper globally
 */
export function configureApiWrapper(config: Partial<ApiWrapperConfig>): void {
  globalConfig = { ...globalConfig, ...config };
}

/**
 * Safely execute an API call and return a standardized result
 */
export async function safeApiCall<T>(
  apiCall: () => Promise<{ data: T }>,
  context?: string,
  config?: Partial<ApiWrapperConfig>
): Promise<ApiResult<T>> {
  const effectiveConfig = { ...globalConfig, ...config };

  try {
    const response = await apiCall();
    return { success: true, data: response.data };
  } catch (error) {
    let apiError: WeTreeApiError;

    if (error instanceof AxiosError) {
      apiError = WeTreeApiError.fromAxiosError(error);
    } else if (error instanceof WeTreeApiError) {
      apiError = error;
    } else {
      // Handle unexpected errors
      apiError = new WeTreeApiError(
        'UNKNOWN_ERROR',
        error instanceof Error ? error.message : 'An unexpected error occurred',
        500,
        undefined,
        error
      );
    }

    // Log the error if configured to do so
    if (effectiveConfig.logErrors && effectiveConfig.errorLogger) {
      effectiveConfig.errorLogger(apiError, context);
    }

    return { success: false, error: apiError };
  }
}

/**
 * Generic wrapper for direct fetch calls with standardized error handling.
 * Returns an ApiResult object, which is either { success: true, data: T } or { success: false, error: WeTreeApiError }.
 *
 * @param fetchCall The actual fetch call function that returns a Promise of Response.
 * @param context Optional string to provide more context in error logs.
 * @returns A Promise resolving to an ApiResult<T>.
 */
export async function safeFetchCall<T>(
  fetchCall: () => Promise<Response>,
  context?: string,
  config?: Partial<ApiWrapperConfig>
): Promise<ApiResult<T>> {
  const mergedConfig = { ...defaultConfig, ...config };

  try {
    const response = await fetchCall();
    if (!response.ok) {
      let errorData: { error?: { code?: string; message?: string; details?: unknown }; message?: string } | unknown;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: response.statusText };
      }

      const errorObj =
        errorData && typeof errorData === 'object' && 'error' in errorData
          ? (errorData as { error?: { code?: string; message?: string; details?: unknown } })
          : null;

      const apiError = new WeTreeApiError(
        errorObj?.error?.code || 'HTTP_ERROR',
        errorObj?.error?.message ||
          (typeof errorData === 'object' && errorData !== null && 'message' in errorData
            ? String((errorData as { message?: string }).message)
            : response.statusText),
        response.status,
        errorObj?.error?.details
      );

      if (mergedConfig.logErrors) {
        if (mergedConfig.errorLogger) {
          mergedConfig.errorLogger(apiError, context);
        } else {
          // eslint-disable-next-line no-console
          console.error(`Fetch API Error${context ? ` in ${context}` : ''}:`, apiError);
        }
      }

      return { success: false, error: apiError };
    }

    const data: T = await response.json();
    return { success: true, data };
  } catch (error: unknown) {
    const apiError =
      error instanceof WeTreeApiError
        ? error
        : new WeTreeApiError(
            'NETWORK_ERROR',
            error instanceof Error ? error.message : 'Network request failed',
            0,
            undefined,
            error
          );

    if (mergedConfig.logErrors) {
      if (mergedConfig.errorLogger) {
        mergedConfig.errorLogger(apiError, context);
      } else {
        // eslint-disable-next-line no-console
        console.error(`Fetch API Error${context ? ` in ${context}` : ''}:`, apiError);
      }
    }

    return { success: false, error: apiError };
  }
}

/**
 * Utility to handle the common pattern of getting API configuration
 */
export async function withApiSetup<TApi, TResult, TConfig = unknown>(
  createApi: (config: TConfig) => TApi,
  getApiConfiguration: () => Promise<TConfig | undefined>,
  operation: (api: TApi) => Promise<{ data: TResult }>
): Promise<ApiResult<TResult>> {
  try {
    const configuration = await getApiConfiguration();
    if (!configuration) {
      return {
        success: false,
        error: new ConfigurationError('Failed to get API configuration')
      };
    }

    const api = createApi(configuration);

    return await safeApiCall(() => operation(api));
  } catch (error) {
    return {
      success: false,
      error: error instanceof WeTreeApiError ? error : new ConfigurationError('Failed to set up API configuration')
    };
  }
}

/**
 * Retry configuration for API calls
 */
export interface RetryConfig {
  /** Maximum number of retry attempts */
  maxRetries: number;
  /** Delay between retries in milliseconds */
  retryDelay: number;
  /** Whether to use exponential backoff */
  exponentialBackoff: boolean;
  /** Function to determine if an error should be retried */
  shouldRetry: (error: WeTreeApiError, attempt: number) => boolean;
}

/**
 * Default retry configuration
 */
const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000,
  exponentialBackoff: true,
  shouldRetry: (error: WeTreeApiError, attempt: number) => {
    // Retry on network errors and 5xx server errors
    return (error.isNetworkError() || (error.status >= 500 && error.status < 600)) && attempt < 3;
  }
};

/**
 * Execute an API call with retry logic
 */
export async function safeApiCallWithRetry<T>(
  apiCall: () => Promise<{ data: T }>,
  context?: string,
  retryConfig: Partial<RetryConfig> = {}
): Promise<ApiResult<T>> {
  const config = { ...defaultRetryConfig, ...retryConfig };
  let lastError: WeTreeApiError | undefined;

  for (let attempt = 1; attempt <= config.maxRetries + 1; attempt++) {
    const result = await safeApiCall(apiCall, context, { logErrors: false });

    if (result.success) {
      return result;
    }

    lastError = result.error;

    // Don't retry if this is the last attempt or if we shouldn't retry this error
    if (attempt > config.maxRetries || !config.shouldRetry(result.error, attempt)) {
      break;
    }

    // Calculate delay with optional exponential backoff
    const delay = config.exponentialBackoff ? config.retryDelay * Math.pow(2, attempt - 1) : config.retryDelay;

    // Wait before retrying
    await new Promise((resolve) => setTimeout(resolve, delay));
  }

  // Log the final error
  if (lastError && globalConfig.logErrors && globalConfig.errorLogger) {
    globalConfig.errorLogger(lastError, `${context} (after ${config.maxRetries} retries)`);
  }

  return {
    success: false,
    error: lastError || new WeTreeApiError('UNKNOWN_ERROR', 'All retries failed', 500)
  };
}
